// lib/csvAnmeldung.js — CSV-Datei für die Sammelanmeldung lesen (18.10.2026)
//
// Ohne neues Paket (Regel): eine kleine eigene Zerlegung. Kann
//   - Semikolon (Excel, deutsch) und Komma als Trenner, erkannt an der Kopfzeile,
//   - Felder in Anführungszeichen, darin "" für ein Anführungszeichen und
//     Zeilenumbrüche,
//   - BOM am Anfang, Windows-Zeilenenden, leere Zeilen.
// Danach wird jede Zeile in die Felder der Anmeldung übersetzt. Geprüft wird
// hier nur die FORM (Datum lesbar, Geschlecht bekannt, Gewicht eine Zahl,
// mindestens ein Code). Ob ein Sportler in eine Kategorie passt, prüft danach
// der Server mit denselben Regeln wie jede andere Anmeldung.
//
// CommonJS wie kategorieText.js, damit der Backend-Test
// (backend/utils/__tests__/csvAnmeldung.test.js) dieselbe Datei prüft.

const SPALTEN = ['Vorname', 'Nachname', 'Geburtsdatum', 'Geschlecht', 'Gewicht', 'Graduierung', 'Divisionscodes'];

const CSV_VORLAGE = '﻿' + [
  SPALTEN.join(';'),
  'Max;Muster;14.03.2012;m;38,5;Gelb;KUM-108 | WWP-217',
  'Erika;Beispiel;2009-07-01;w;52;Grün;PS-162',
].join('\r\n') + '\r\n';

/** Zerlegt CSV-Text. @returns {{trenner: string, zeilen: string[][]}} */
function csvParsen(text) {
  let t = String(text || '');
  if (t.charCodeAt(0) === 0xfeff) t = t.slice(1);
  const ersteZeile = t.split(/\r?\n/, 1)[0] || '';
  const trenner = (ersteZeile.match(/;/g) || []).length >= (ersteZeile.match(/,/g) || []).length ? ';' : ',';
  const zeilen = [];
  let feld = '';
  let zeile = [];
  let inAnf = false;
  for (let i = 0; i < t.length; i++) {
    const c = t[i];
    if (inAnf) {
      if (c === '"') {
        if (t[i + 1] === '"') { feld += '"'; i++; } else inAnf = false;
      } else feld += c;
    } else if (c === '"' && feld.trim() === '') {
      inAnf = true; feld = '';
    } else if (c === trenner) {
      zeile.push(feld); feld = '';
    } else if (c === '\n' || c === '\r') {
      if (c === '\r' && t[i + 1] === '\n') i++;
      zeile.push(feld); feld = '';
      if (zeile.some((f) => f.trim() !== '')) zeilen.push(zeile);
      zeile = [];
    } else feld += c;
  }
  zeile.push(feld);
  if (zeile.some((f) => f.trim() !== '')) zeilen.push(zeile);
  return { trenner, zeilen: zeilen.map((z) => z.map((f) => f.trim())) };
}

/** TT.MM.JJJJ, T.M.JJJJ oder JJJJ-MM-TT → JJJJ-MM-TT (ohne Zeitzone) oder null */
function datumLesen(wert) {
  const s = String(wert || '').trim();
  let j; let m; let d;
  let r = /^(\d{1,2})\.(\d{1,2})\.(\d{4})$/.exec(s);
  if (r) { [, d, m, j] = r; } else {
    r = /^(\d{4})-(\d{1,2})-(\d{1,2})$/.exec(s);
    if (!r) return null;
    [, j, m, d] = r;
  }
  const jj = Number(j); const mm = Number(m); const dd = Number(d);
  const probe = new Date(Date.UTC(jj, mm - 1, dd));
  if (probe.getUTCFullYear() !== jj || probe.getUTCMonth() !== mm - 1 || probe.getUTCDate() !== dd) return null;
  return `${jj}-${String(mm).padStart(2, '0')}-${String(dd).padStart(2, '0')}`;
}

/** m/w, männlich/weiblich, male/female, Junge/Mädchen … → 'männlich' | 'weiblich' | null */
function geschlechtLesen(wert) {
  const s = String(wert || '').trim().toLowerCase();
  if (['m', 'männlich', 'maennlich', 'male', 'mann', 'junge', 'herr'].includes(s)) return 'männlich';
  if (['w', 'weiblich', 'female', 'f', 'frau', 'mädchen', 'maedchen', 'dame'].includes(s)) return 'weiblich';
  return null;
}

/** „38,5“ oder „38.5 kg“ → 38.5; leer → null; Unsinn → NaN */
function gewichtLesen(wert) {
  const s = String(wert || '').trim().replace(/\s*kg$/i, '').replace(',', '.');
  if (!s) return null;
  return /^\d+(\.\d+)?$/.test(s) ? Number(s) : NaN;
}

/** Codes mit | trennen (Codes können Leerzeichen enthalten, z. B. „K1FC -138“).
 *  Ist das Komma nicht der Spaltentrenner, geht auch das Komma. */
function codesLesen(wert, trenner) {
  const muster = trenner === ',' ? /[|]/ : /[|,]/;
  return [...new Set(String(wert || '').split(muster).map((c) => c.trim()).filter(Boolean))];
}

const norm = (s) => String(s || '').trim().toLowerCase().replace(/\s+/g, ' ');

/**
 * CSV → Einträge mit Fehlerliste je Zeile.
 * @returns {{trenner:string, fehler:string[], eintraege:Array<{zeile:number, vorname:string, nachname:string,
 *   geburtsdatum:string|null, geschlecht:string|null, gewicht:number|null, graduierung:string, codes:string[], fehler:string[]}>}}
 */
function csvAuswerten(text) {
  const { trenner, zeilen } = csvParsen(text);
  const fehler = [];
  if (!zeilen.length) return { trenner, fehler: ['Die Datei ist leer.'], eintraege: [] };
  const kopf = zeilen[0].map(norm);
  const index = {};
  for (const s of SPALTEN) index[s] = kopf.indexOf(norm(s));
  const fehlend = SPALTEN.filter((s) => index[s] < 0 && s !== 'Graduierung');
  if (fehlend.length) {
    return { trenner, fehler: [`In der Kopfzeile fehlen Spalten: ${fehlend.join(', ')}. Bitte die Vorlage verwenden.`], eintraege: [] };
  }
  const wert = (z, s) => (index[s] >= 0 ? (z[index[s]] || '') : '');
  const gesehen = new Map();
  const eintraege = zeilen.slice(1).map((z, i) => {
    const e = {
      zeile: i + 2,
      vorname: wert(z, 'Vorname'),
      nachname: wert(z, 'Nachname'),
      geburtsdatum: datumLesen(wert(z, 'Geburtsdatum')),
      geschlecht: geschlechtLesen(wert(z, 'Geschlecht')),
      gewicht: gewichtLesen(wert(z, 'Gewicht')),
      graduierung: wert(z, 'Graduierung'),
      codes: codesLesen(wert(z, 'Divisionscodes'), trenner),
      fehler: [],
    };
    if (e.vorname.length < 2) e.fehler.push('Vorname fehlt');
    if (e.nachname.length < 2) e.fehler.push('Nachname fehlt');
    if (!e.geburtsdatum) e.fehler.push(`Geburtsdatum „${wert(z, 'Geburtsdatum')}“ nicht lesbar (TT.MM.JJJJ)`);
    if (!e.geschlecht) e.fehler.push(`Geschlecht „${wert(z, 'Geschlecht')}“ unbekannt (m oder w)`);
    if (Number.isNaN(e.gewicht)) e.fehler.push(`Gewicht „${wert(z, 'Gewicht')}“ ist keine Zahl`);
    else if (e.gewicht !== null && (e.gewicht < 15 || e.gewicht > 250)) e.fehler.push('Gewicht muss zwischen 15 und 250 kg liegen');
    if (!e.codes.length) e.fehler.push('Keine Divisionscodes');
    const schluessel = `${norm(e.vorname)}|${norm(e.nachname)}|${e.geburtsdatum}`;
    if (gesehen.has(schluessel)) e.fehler.push(`Doppelt (schon in Zeile ${gesehen.get(schluessel)})`);
    else gesehen.set(schluessel, e.zeile);
    return e;
  });
  if (!eintraege.length) fehler.push('Unter der Kopfzeile steht keine Zeile.');
  return { trenner, fehler, eintraege };
}

/** Passt ein Eintrag zu einem vorhandenen Wettkämpfer? (Vorname, Nachname, Geburtsdatum) */
function gleicherSportler(e, w) {
  return norm(e.vorname) === norm(w.vorname) && norm(e.nachname) === norm(w.nachname)
    && e.geburtsdatum === String(w.geburtsdatum || '').slice(0, 10);
}

module.exports = { SPALTEN, CSV_VORLAGE, csvParsen, csvAuswerten, datumLesen, geschlechtLesen, gewichtLesen, codesLesen, gleicherSportler };
